import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Trash2, CalendarClock, Check } from "lucide-react";
import { toast } from "sonner";

const fmtDue = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  const label = dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = !isNaN(dt) && dt < today;
  return { label, overdue };
};

export const TasksView = ({ api }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mail/tasks");
      setTasks(data.tasks || []);
    } catch {
      toast.error("Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/mail/tasks", { title: title.trim(), due });
      setTitle("");
      setDue("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add the task");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/mail/tasks/${t.id}`, { done: !t.done });
      setTasks((ts) => ts.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)));
    } catch {
      toast.error("Could not update the task");
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/mail/tasks/${t.id}`);
      setTasks((ts) => ts.filter((x) => x.id !== t.id));
    } catch {
      toast.error("Could not delete the task");
    }
  };

  const visible = tasks.filter((t) => (filter === "open" ? !t.done : filter === "done" ? t.done : true));
  const openCount = tasks.filter((t) => !t.done).length;

  return (
    <div className="h-full flex flex-col" data-testid="tasks-view">
      <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-white/10 flex-wrap">
        <h2 className="font-marcellus text-xl text-white">To Do</h2>
        <span className="text-xs text-slate-500" data-testid="tasks-open-count">{openCount} open</span>
        <div className="ml-auto flex items-center gap-1">
          {["all", "open", "done"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[11px] tracking-wide uppercase px-3 py-1.5 transition-colors border ${filter === f ? "border-nx-gold/60 text-nx-goldlight" : "border-transparent text-slate-500 hover:text-white"}`}
              data-testid={`tasks-filter-${f}`}
            >
              {f === "all" ? "All" : f === "open" ? "Open" : "Done"}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={add} className="flex items-stretch gap-2 px-4 sm:px-6 py-3 border-b border-white/10" data-testid="tasks-add-form">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a task — e.g. Send quotation to Sharma Electricals"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors"
          data-testid="tasks-add-title"
        />
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="bg-white/[0.04] border border-white/15 text-slate-100 px-3 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 [color-scheme:dark]"
          aria-label="Due date (optional)"
          data-testid="tasks-add-due"
        />
        <button
          type="submit"
          disabled={busy || !title.trim()}
          className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-wide px-5 transition-colors disabled:opacity-60"
          data-testid="tasks-add-button"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500 text-sm" data-testid="tasks-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-slate-600" data-testid="tasks-empty">
            <Check size={28} strokeWidth={1.2} />
            <p className="text-sm">{filter === "done" ? "Nothing completed yet" : "All clear — no tasks"}</p>
          </div>
        ) : (
          visible.map((t) => {
            const d = fmtDue(t.due);
            return (
              <div key={t.id} className="group flex items-center gap-3 px-4 sm:px-6 py-3.5 border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`task-${t.id}`}>
                <button
                  onClick={() => toggle(t)}
                  className={`w-5 h-5 shrink-0 border flex items-center justify-center transition-colors ${t.done ? "bg-nx-gold border-nx-gold text-nx-navy" : "border-white/25 hover:border-nx-gold"}`}
                  aria-label={t.done ? "Mark as not done" : "Mark as done"}
                  data-testid={`task-toggle-${t.id}`}
                >
                  {t.done && <Check size={13} strokeWidth={3} />}
                </button>
                <span className={`flex-1 min-w-0 truncate text-sm ${t.done ? "text-slate-600 line-through" : "text-slate-200"}`}>{t.title}</span>
                {d && (
                  <span className={`flex items-center gap-1.5 text-[11px] shrink-0 ${d.overdue && !t.done ? "text-red-400" : "text-slate-500"}`} data-testid={`task-due-${t.id}`}>
                    <CalendarClock size={12} /> {d.label}
                  </span>
                )}
                <button
                  onClick={() => remove(t)}
                  className="p-1.5 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                  aria-label="Delete task"
                  data-testid={`task-delete-${t.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
